import { Listbox, Transition } from '@headlessui/react';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';
import React, { Fragment, useState, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import SearchIcon from 'assets/SearchIcon';
import { usePopper } from 'hooks/usePopper';
import { t } from 'locales/i18n';

export interface SelectMenuOption {
  value: string | number;
  primaryText: string;
  secondaryText?: string;
}

interface SelectMenuProps {
  options: SelectMenuOption[];
  emptyText?: string;
  value?: SelectMenuOption | null;
  onChange: (value: SelectMenuOption) => void;
  isDisabled?: boolean;
  withSearch?: boolean;
  width?: number;
  dataQa?: string;
}

const SelectMenu = ({
  options,
  emptyText = t('Select one'),
  value,
  onChange,
  isDisabled = false,
  withSearch = false,
  width = 300,
  dataQa,
}: SelectMenuProps) => {
  const containsSecondaryText = options.some((option) => option.secondaryText);

  const listBoxButtonClassName = (open: boolean) =>
    twMerge(
      'relative',
      `w-[${width}px]`,
      'cursor-default',
      'bg-white',
      'p-1',
      'pr-12',
      'text-left',
      'text-kira-slate-900',
      'border-b',
      open ? 'border-kira-blue-800' : 'border-kira-slate-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-kira-blue-800',
      'focus:rounded',
      'sm:text-sm sm:leading-6',
      isDisabled && 'cursor-not-allowed'
    );

  const chevronIconWrapperClassName = twMerge(
    'pointer-events-none',
    'absolute',
    'inset-y-0',
    'right-0',
    'flex',
    'items-center',
    'pr-2',
    isDisabled && 'text-kira-slate-700 opacity-50'
  );

  const listBoxOptionsClassName = twMerge(
    'fixed',
    'w-[300px]',
    'z-10',
    'mt-1',
    'max-h-60',
    'overflow-auto',
    'rounded-md',
    'bg-white',
    'text-base',
    'shadow-lg',
    'ring-1',
    'ring-black',
    'ring-opacity-5',
    'focus:outline-none',
    'sm:text-sm',
    withSearch ? 'pb-1' : 'py-1'
  );

  const listBoxOptionClassName = (active: boolean) =>
    twMerge(
      'text-kira-slate-900',
      active && 'bg-kira-blue-100',
      'relative',
      'cursor-pointer',
      'select-none',
      'py-2',
      'pl-8',
      'pr-4'
    );

  const primaryTextClassName = (selected: boolean) =>
    twMerge(
      containsSecondaryText ? 'prose-body-md-med' : 'prose-body-sm-reg',
      selected && !containsSecondaryText && 'font-medium text-kira-blue-800',
      'block'
    );

  const checkIconClassName = twMerge(
    'text-kira-blue-900',
    'absolute',
    'inset-y-0',
    'left-0',
    'flex',
    'items-center',
    'pl-1.5'
  );

  const selectionTextClassName = twMerge(
    'block',
    'truncate',
    'prose-body-md-reg',
    !value?.primaryText && 'text-kira-slate-700',
    isDisabled && 'text-kira-slate-900 opacity-50'
  );

  const searchBoxWrapperClassName = twMerge(
    'sticky',
    'top-0',
    'z-10',
    'bg-white',
    'p-1'
  );

  const searchIconWrapperClassName = twMerge(
    'pointer-events-none',
    'absolute',
    'inset-y-0',
    'left-0',
    'flex',
    'items-center',
    'pl-3'
  );

  const searchInputClassName = twMerge(
    'block',
    'w-full',
    'rounded-lg',
    'border',
    'border-gray-300',
    'p-2',
    'pl-10',
    'text-sm',
    'text-gray-900',
    'focus:border-blue-500',
    'focus:ring-blue-500'
  );

  const handleSearch = (text: string) =>
    text.toLowerCase().includes(searchTerm.toLowerCase());
  const [searchTerm, setSearchTerm] = useState('');

  const [trigger, container] = usePopper({
    placement: 'bottom-end',
    strategy: 'fixed',
    modifiers: [{ name: 'offset', options: { offset: [0, 10] } }],
  });

  return (
    <Listbox disabled={isDisabled} value={value} onChange={onChange}>
      {({ open }) => {
        useEffect(() => {
          if (withSearch && open) {
            setSearchTerm('');
          }
        }, [open]);

        return (
          <div className="relative mt-2">
            <Listbox.Button
              ref={trigger}
              className={listBoxButtonClassName(open)}
              title={value?.primaryText}
              data-testid={dataQa}
            >
              <span className={selectionTextClassName}>
                {value?.primaryText || emptyText}
              </span>
              <span className={chevronIconWrapperClassName}>
                {open ? (
                  <ChevronUpIcon
                    className="h-5 w-5 text-kira-slate-700"
                    aria-hidden="true"
                  />
                ) : (
                  <ChevronDownIcon
                    className="h-5 w-5 text-kira-slate-700"
                    aria-hidden="true"
                  />
                )}
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                ref={container}
                className={listBoxOptionsClassName}
              >
                {withSearch && (
                  <div className={searchBoxWrapperClassName}>
                    <div className={searchIconWrapperClassName}>
                      <SearchIcon />
                    </div>
                    <input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      type="text"
                      className={searchInputClassName}
                      placeholder="Search..."
                      required
                    />
                  </div>
                )}
                {options
                  .filter((option) => handleSearch(option.primaryText))
                  .map((option, index) => (
                    <Listbox.Option
                      key={option.value}
                      className={({ active }) => listBoxOptionClassName(active)}
                      value={option}
                      data-testid={`option-${index}`}
                    >
                      {({ selected }) => (
                        <div>
                          <span className={primaryTextClassName(selected)}>
                            {option.primaryText}
                          </span>

                          {selected ? (
                            <span className={checkIconClassName}>
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}

                          {option.secondaryText && (
                            <p className="prose-body-sm-reg text-kira-slate-700">
                              {option.secondaryText}
                            </p>
                          )}
                        </div>
                      )}
                    </Listbox.Option>
                  ))}
              </Listbox.Options>
            </Transition>
          </div>
        );
      }}
    </Listbox>
  );
};

export default SelectMenu;
